import type { CurrentOrganizationRole } from '@/auth/currentIdentity';

/**
 * The parts of the demo session that change while the demo is open.
 *
 * The demo has no backend, so `demoSdk` and `useDemoAuth` would otherwise be
 * frozen literals: editing your name on `/account` would flash "saved" and then
 * snap back, because the form re-seeds from the profile it just failed to
 * change. This module is the small mutable middle those two share, plus the one
 * field the org mocks can move (`orgRole`, via transfer-ownership).
 *
 * Deliberately narrow: it holds what the REAL auth path holds — profile fields
 * and the caller's own org role — and nothing else. The org and member records
 * themselves live with the other demo fixtures in `src/mocks/`, which this
 * module must not import (see `demoMode.ts`).
 *
 * State is module-scope and therefore per page load. A reload reseeds, which is
 * the demo's reset button.
 */
export interface DemoSession {
  firstName: string;
  lastName: string;
  /** The demo visitor's role in `DEMO_ORG_ID`. Moves only on transfer-ownership. */
  orgRole: CurrentOrganizationRole;
}

const INITIAL: DemoSession = {
  firstName: 'Demo',
  lastName: 'User',
  orgRole: 'owner',
};

// `useSyncExternalStore` calls getSnapshot on every render and bails out of
// re-rendering only when the result is referentially equal to last time, so the
// snapshot has to be a cached object replaced on write — never rebuilt per call.
let snapshot: DemoSession = INITIAL;

const listeners = new Set<() => void>();

export function subscribeDemoSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getDemoSessionSnapshot(): DemoSession {
  return snapshot;
}

/**
 * The demo profile's display name, or `undefined` when it would be empty.
 *
 * One derivation for its two readers (`demoSdk.name` and `useDemoAuth`), which
 * have to agree — that agreement is the whole reason the SDK's profile fields
 * are getters over this store rather than literals.
 *
 * `undefined` rather than `''` mirrors the consumer bridge, and is the part
 * that matters: `displayName()` falls back with `??`, so an empty string is not
 * a missing name — it renders as a blank greeting that no fallback rescues.
 * Clearing both name fields on `/account` is the reachable path.
 */
export function demoDisplayName(session: DemoSession): string | undefined {
  return `${session.firstName} ${session.lastName}`.trim() || undefined;
}

function commit(next: DemoSession): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

export function setDemoProfile(firstName: string, lastName: string): void {
  commit({ ...snapshot, firstName, lastName });
}

export function setDemoOrgRole(orgRole: CurrentOrganizationRole): void {
  commit({ ...snapshot, orgRole });
}

/** Back to a freshly-loaded demo. For tests — the app resets by reloading. */
export function resetDemoSession(): void {
  commit(INITIAL);
}
